import React, { useState, useEffect } from 'react';
import { FaTimes, FaTicketAlt, FaUser, FaCalendarAlt, FaEye, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaBan, FaCheck, FaSearch, FaPlus, FaDownload, FaFilter } from 'react-icons/fa';

import { 
    getTicketsByEvento, 
    createTicket, 
    updateTicket, 
    deleteTicket, 
    toggleTicketStatus, 
    cancelTicket, 
    markTicketAsUsed,
    getTicketStatsByEvento 
} from '../../../../../components/http/Actions/TicketsAction';

import { getAllSeccionesByEventoForAdmin } from '../../../../../components/http/Actions/SeccionEventoAction';

const TicketEventoModal = ({ evento, onClose, showNotification }) => {
    const [tickets, setTickets] = useState([]);
    const [secciones, setSecciones] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estados para filtros y búsqueda
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEstado, setFilterEstado] = useState('');
    const [filterSeccion, setFilterSeccion] = useState('');
    
    // Estados para crear ticket
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingTicket, setEditingTicket] = useState(null);
    const [formData, setFormData] = useState({
        usuarioId: '',
        precio: '',
        seccionEventoId: ''
    });

    const token = localStorage.getItem("token");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Cargar tickets
            await getTicketsByEvento(
                evento.eventoId,
                setTickets,
                setError,
                token
            );

            // Cargar secciones
            await getAllSeccionesByEventoForAdmin(
                evento.eventoId,
                setSecciones,
                setError,
                token
            );

            // Cargar estadísticas
            await getTicketStatsByEvento(
                evento.eventoId,
                setStats,
                setError,
                token
            );
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Formatear fecha
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Formatear precio
    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    // Limpiar precio para enviar al backend
    const cleanPrice = (priceText) => {
        return priceText.replace(/[^\d]/g, '');
    };

    // Filtrar tickets
    const filteredTickets = tickets.filter(ticket => {
        const matchesSearch = !searchTerm || 
            ticket.codigoTicket.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.usuario?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ticket.usuario?.email?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesEstado = !filterEstado || ticket.estadoTicket === filterEstado;
        const matchesSeccion = !filterSeccion || ticket.seccionEvento?.seccionEventoId === filterSeccion;
        
        return matchesSearch && matchesEstado && matchesSeccion;
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'precio') {
            const numericValue = value.replace(/[^\d]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue ? new Intl.NumberFormat('es-CL').format(parseInt(numericValue)) : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const dataToSend = {
            eventoId: evento.eventoId,
            usuarioId: formData.usuarioId,
            precio: parseFloat(cleanPrice(formData.precio)),
            seccionEventoId: formData.seccionEventoId
        };

        try {
            if (editingTicket) {
                await updateTicket(
                    editingTicket.ticketId,
                    {
                        ...dataToSend,
                        estadoTicket: editingTicket.estadoTicket,
                        habilitado: editingTicket.habilitado
                    },
                    setError,
                    token
                );
                showNotification('Ticket actualizado exitosamente', 'success');
            } else {
                await createTicket(dataToSend, setError, token);
                showNotification('Ticket creado exitosamente', 'success');
            }

            setFormData({
                usuarioId: '',
                precio: '',
                seccionEventoId: ''
            });
            setShowCreateForm(false);
            setEditingTicket(null);
            await fetchData();
        } catch (error) {
            console.error('Error saving ticket:', error);
            showNotification('Error al guardar el ticket', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (ticket) => {
        setEditingTicket(ticket);
        setFormData({
            usuarioId: ticket.usuarioId,
            precio: new Intl.NumberFormat('es-CL').format(ticket.precio),
            seccionEventoId: ticket.seccionEventoId
        });
        setShowCreateForm(true);
    };

    const handleDelete = async (ticketId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este ticket?')) {
            try {
                await deleteTicket(ticketId, setError, token);
                showNotification('Ticket eliminado exitosamente', 'success');
                await fetchData();
            } catch (error) {
                showNotification('Error al eliminar el ticket', 'error');
            }
        }
    };

    const handleToggleStatus = async (ticketId) => {
        try {
            await toggleTicketStatus(ticketId, setError, token);
            showNotification('Estado del ticket actualizado', 'success');
            await fetchData();
        } catch (error) {
            showNotification('Error al cambiar el estado', 'error');
        }
    };

    const handleCancelTicket = async (ticketId) => {
        if (window.confirm('¿Estás seguro de que quieres cancelar este ticket?')) {
            try {
                await cancelTicket(ticketId, setError, token);
                showNotification('Ticket cancelado exitosamente', 'success');
                await fetchData();
            } catch (error) {
                showNotification('Error al cancelar el ticket', 'error');
            }
        }
    };

    const handleMarkAsUsed = async (ticketId) => {
        if (window.confirm('¿Confirmas que este ticket fue usado en el evento?')) {
            try {
                await markTicketAsUsed(ticketId, setError, token);
                showNotification('Ticket marcado como usado', 'success');
                await fetchData();
            } catch (error) {
                showNotification('Error al marcar el ticket como usado', 'error');
            }
        }
    };

    const cancelForm = () => {
        setShowCreateForm(false);
        setEditingTicket(null);
        setFormData({
            usuarioId: '',
            precio: '',
            seccionEventoId: ''
        });
    };

    const getEstadoBadge = (estado) => {
        const badges = {
            'Activo': 'bg-green-600/20 text-green-300 border-green-600/50',
            'Usado': 'bg-blue-600/20 text-blue-300 border-blue-600/50',
            'Cancelado': 'bg-red-600/20 text-red-300 border-red-600/50'
        };
        return badges[estado] || 'bg-gray-600/20 text-gray-300 border-gray-600/50';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
            <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-7xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-2xl font-bold text-white mb-2 flex items-center">
                            <FaTicketAlt className="mr-3 text-red-500" />
                            Gestionar Tickets
                        </h3>
                        <div className="text-gray-300">
                            <p className="font-medium">{evento.titulo}</p>
                            <p className="text-sm text-gray-400">
                                {formatDate(evento.fecha)} - {evento.lugar?.nombre}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Estadísticas */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-zinc-700/50 rounded-lg p-4 border border-zinc-600">
                            <div className="text-gray-400 text-sm">Total Vendidos</div>
                            <div className="text-2xl font-bold text-white">{stats.totalTicketsVendidos}</div>
                        </div>
                        <div className="bg-zinc-700/50 rounded-lg p-4 border border-zinc-600">
                            <div className="text-gray-400 text-sm">Usados</div>
                            <div className="text-2xl font-bold text-blue-400">{stats.totalTicketsUsados}</div>
                        </div>
                        <div className="bg-zinc-700/50 rounded-lg p-4 border border-zinc-600">
                            <div className="text-gray-400 text-sm">Activos</div>
                            <div className="text-2xl font-bold text-green-400">{stats.totalTicketsActivos}</div>
                        </div>
                        <div className="bg-zinc-700/50 rounded-lg p-4 border border-zinc-600">
                            <div className="text-gray-400 text-sm">Ingresos Totales</div>
                            <div className="text-2xl font-bold text-yellow-400">{formatPrice(stats.ingresosTotales)}</div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {/* Controles */}
                <div className="flex flex-wrap gap-4 mb-6">
                    {!showCreateForm && (
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
                        >
                            <FaPlus className="mr-2" />
                            Nuevo Ticket
                        </button>
                    )}

                    {/* Filtros */}
                    <div className="flex flex-wrap gap-3 flex-1">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por código, usuario..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                            />
                        </div>

                        <select
                            value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)}
                            className="px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        >
                            <option value="">Todos los estados</option>
                            <option value="Activo">Activo</option>
                            <option value="Usado">Usado</option>
                            <option value="Cancelado">Cancelado</option>
                        </select>

                        <select
                            value={filterSeccion}
                            onChange={(e) => setFilterSeccion(e.target.value)}
                            className="px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                        >
                            <option value="">Todas las secciones</option>
                            {secciones.map(seccion => (
                                <option key={seccion.seccionEventoId} value={seccion.seccionEventoId}>
                                    {seccion.nombreSeccion}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Formulario de crear/editar */}
                {showCreateForm && (
                    <div className="bg-zinc-700/50 rounded-lg p-6 mb-6 border border-zinc-600">
                        <h4 className="text-lg font-semibold text-white mb-4">
                            {editingTicket ? 'Editar Ticket' : 'Nuevo Ticket'}
                        </h4>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        ID de Usuario *
                                    </label>
                                    <input
                                        type="text"
                                        name="usuarioId"
                                        value={formData.usuarioId}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="ID del usuario"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Precio *
                                    </label>
                                    <input
                                        type="text"
                                        name="precio"
                                        value={formData.precio}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="15.000"
                                       required
                                   />
                               </div>
                               <div>
                                   <label className="block text-sm font-medium text-gray-300 mb-2">
                                       Sección *
                                   </label>
                                   <select
                                       name="seccionEventoId"
                                       value={formData.seccionEventoId}
                                       onChange={handleInputChange}
                                       className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                       required
                                   >
                                       <option value="">Seleccionar sección</option>
                                       {secciones.filter(s => s.habilitado).map(seccion => (
                                           <option key={seccion.seccionEventoId} value={seccion.seccionEventoId}>
                                               {seccion.nombreSeccion} - {formatPrice(seccion.precio)} 
                                               ({seccion.entradasRestantes} disponibles)
                                           </option>
                                       ))}
                                   </select>
                               </div>
                           </div>
                           <div className="flex justify-end space-x-3">
                               <button
                                   type="button"
                                   onClick={cancelForm}
                                   className="px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-colors"
                               >
                                   Cancelar
                               </button>
                               <button
                                   type="submit"
                                   disabled={loading}
                                   className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                               >
                                   {loading ? 'Guardando...' : editingTicket ? 'Actualizar' : 'Crear'}
                               </button>
                           </div>
                       </form>
                   </div>
               )}

               {/* Lista de tickets */}
               <div>
                   <h4 className="text-lg font-semibold text-white mb-4">
                       Tickets del Evento ({filteredTickets.length})
                   </h4>
                   
                   {loading && tickets.length === 0 ? (
                       <div className="flex justify-center py-8">
                           <div className="w-8 h-8 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                       </div>
                   ) : filteredTickets.length === 0 ? (
                       <div className="text-center py-8 text-gray-400">
                           <FaTicketAlt className="mx-auto text-4xl mb-4 opacity-50" />
                           <p>No hay tickets que coincidan con los filtros</p>
                           <p className="text-sm">Ajusta los filtros o crea el primer ticket</p>
                       </div>
                   ) : (
                       <div className="overflow-x-auto">
                           <table className="w-full text-sm text-gray-300">
                               <thead className="text-xs uppercase bg-zinc-700/50 text-gray-400">
                                   <tr>
                                       <th className="px-4 py-3">Código</th>
                                       <th className="px-4 py-3">Usuario</th>
                                       <th className="px-4 py-3">Sección</th>
                                       <th className="px-4 py-3">Precio</th>
                                       <th className="px-4 py-3">Estado</th>
                                       <th className="px-4 py-3">Fecha Compra</th>
                                       <th className="px-4 py-3">Fecha Uso</th>
                                       <th className="px-4 py-3">Acciones</th>
                                   </tr>
                               </thead>
                               <tbody>
                                   {filteredTickets.map((ticket, index) => (
                                       <tr 
                                           key={ticket.ticketId} 
                                           className={`border-b border-zinc-700 hover:bg-zinc-700/30 transition-colors ${
                                               index % 2 === 0 ? 'bg-zinc-800/50' : 'bg-zinc-700/20'
                                           }`}
                                       >
                                           <td className="px-4 py-3">
                                               <div className="font-mono text-xs bg-zinc-700 px-2 py-1 rounded">
                                                   {ticket.codigoTicket}
                                               </div>
                                           </td>
                                           <td className="px-4 py-3">
                                               <div>
                                                   <div className="font-medium text-white">
                                                       {ticket.usuario?.nombre || 'N/A'} {ticket.usuario?.apellido || ''}
                                                   </div>
                                                   <div className="text-xs text-gray-400">
                                                       {ticket.usuario?.email}
                                                   </div>
                                               </div>
                                           </td>
                                           <td className="px-4 py-3">
                                               <span className="bg-blue-600/20 text-blue-300 px-2 py-1 rounded text-xs">
                                                   {ticket.seccionEvento?.nombreSeccion || 'N/A'}
                                               </span>
                                           </td>
                                           <td className="px-4 py-3 font-medium text-white">
                                               {formatPrice(ticket.precio)}
                                           </td>
                                           <td className="px-4 py-3">
                                               <span className={`px-2 py-1 rounded text-xs border ${getEstadoBadge(ticket.estadoTicket)}`}>
                                                   {ticket.estadoTicket}
                                               </span>
                                           </td>
                                           <td className="px-4 py-3">
                                               <div className="text-xs">
                                                   {formatDate(ticket.fechaCompra)}
                                               </div>
                                           </td>
                                           <td className="px-4 py-3">
                                               <div className="text-xs">
                                                   {ticket.fechaUso ? formatDate(ticket.fechaUso) : '-'}
                                               </div>
                                           </td>
                                           <td className="px-4 py-3">
                                               <div className="flex space-x-1">
                                                   {/* Usar ticket */}
                                                   {ticket.estadoTicket === 'Activo' && ticket.habilitado && (
                                                       <button
                                                           onClick={() => handleMarkAsUsed(ticket.ticketId)}
                                                           className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                                           title="Marcar como usado"
                                                       >
                                                           <FaCheck />
                                                       </button>
                                                   )}
                                                   
                                                   {/* Cancelar ticket */}
                                                   {ticket.estadoTicket === 'Activo' && (
                                                       <button
                                                           onClick={() => handleCancelTicket(ticket.ticketId)}
                                                           className="p-1 text-orange-400 hover:text-orange-300 transition-colors"
                                                           title="Cancelar ticket"
                                                       >
                                                           <FaBan />
                                                       </button>
                                                   )}
                                                   
                                                   {/* Toggle status */}
                                                   <button
                                                       onClick={() => handleToggleStatus(ticket.ticketId)}
                                                       className={`p-1 transition-colors ${
                                                           ticket.habilitado 
                                                               ? 'text-yellow-400 hover:text-yellow-300' 
                                                               : 'text-gray-400 hover:text-gray-300'
                                                       }`}
                                                       title={ticket.habilitado ? 'Deshabilitar' : 'Habilitar'}
                                                   >
                                                       {ticket.habilitado ? <FaToggleOn /> : <FaToggleOff />}
                                                   </button>
                                                   
                                                   {/* Editar */}
                                                   <button
                                                       onClick={() => handleEdit(ticket)}
                                                       className="p-1 text-blue-400 hover:text-blue-300 transition-colors"
                                                       title="Editar"
                                                   >
                                                       <FaEdit />
                                                   </button>
                                                   
                                                   {/* Eliminar */}
                                                   {ticket.estadoTicket !== 'Usado' && (
                                                       <button
                                                           onClick={() => handleDelete(ticket.ticketId)}
                                                           className="p-1 text-red-400 hover:text-red-300 transition-colors"
                                                           title="Eliminar"
                                                       >
                                                           <FaTrash />
                                                       </button>
                                                   )}
                                               </div>
                                           </td>
                                       </tr>
                                   ))}
                               </tbody>
                           </table>
                       </div>
                   )}
               </div>
           </div>
       </div>
   );
};

export default TicketEventoModal;